
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, LifeBuoy, Bug, Lightbulb, ArrowRight, ExternalLink, Hash, ChevronLeft, Store, CircleDollarSign, Languages, Sun, Moon } from 'lucide-react';
import { useTranslation } from '../lib/translations';
import { UserProfile } from '../types';

interface SupportProps {
  profile?: UserProfile | null;
}

const Support: React.FC<SupportProps> = ({ profile }) => {
  const { t, language, setLanguage } = useTranslation();
  const location = useLocation();
  const adminEmail = "axel.indedev@gmail.com";
  const [isDark, setIsDark] = React.useState(document.documentElement.classList.contains('dark'));

  React.useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  const handleEmailSupport = (subject: string) => {
    window.location.href = `mailto:${adminEmail}?subject=${encodeURIComponent(subject)}`;
  };

  const content = (
    <div className="space-y-10">
      <div className="space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-500/20 text-purple-600 dark:text-purple-400 text-[10px] font-black uppercase tracking-[0.2em] shadow-sm mb-2">
          <LifeBuoy size={12} className="animate-spin-slow" /> {t.nav.support}
        </div>
        <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tighter">{t.support.title}</h1>
        <p className="text-slate-500 font-medium text-lg">{t.support.desc}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Report Issue Card */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl p-8 shadow-xl hover:border-red-500/30 transition-all group relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
            <Bug size={80} />
          </div>
          <div className="w-14 h-14 bg-red-50 dark:bg-red-500/10 rounded-xl flex items-center justify-center text-red-600 mb-6">
            <Bug size={28} />
          </div>
          <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-4 tracking-tight">{t.support.reportIssue}</h3>
          <p className="text-slate-500 font-medium text-sm leading-relaxed mb-8">{t.support.reportIssueDesc}</p>
          <button 
            onClick={() => handleEmailSupport("Issue Report - SharpNote")}
            className="flex items-center gap-3 bg-slate-900 dark:bg-slate-800 text-white px-6 py-3 rounded-lg font-black text-[10px] uppercase tracking-widest hover:bg-slate-800 dark:hover:bg-slate-700 transition-all group"
          >
            {t.support.emailUs} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* Suggestion Card */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl p-8 shadow-xl hover:border-amber-500/30 transition-all group relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
            <Lightbulb size={80} />
          </div>
          <div className="w-14 h-14 bg-amber-50 dark:bg-amber-500/10 rounded-xl flex items-center justify-center text-amber-600 mb-6">
            <Lightbulb size={28} />
          </div>
          <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-4 tracking-tight">{t.support.makeSuggestion}</h3>
          <p className="text-slate-500 font-medium text-sm leading-relaxed mb-8">{t.support.makeSuggestionDesc}</p>
          <button 
            onClick={() => handleEmailSupport("Feature Suggestion - SharpNote")}
            className="flex items-center gap-3 bg-slate-900 dark:bg-slate-800 text-white px-6 py-3 rounded-lg font-black text-[10px] uppercase tracking-widest hover:bg-slate-800 dark:hover:bg-slate-700 transition-all group"
          >
            {t.support.emailUs} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>

      <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-white/5 rounded-xl p-10 flex flex-col md:flex-row items-center gap-8 shadow-inner">
        <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center text-white shadow-xl shadow-purple-500/20 shrink-0">
          <Mail size={32} />
        </div>
        <div className="flex-1 text-center md:text-left">
          <h4 className="text-xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">{t.support.contactAdmin}</h4>
          <p className="text-slate-500 font-medium leading-relaxed">
            Reach out directly for urgent matters or general questions.
          </p>
        </div>
        <a 
          href={`mailto:${adminEmail}`}
          className="flex items-center gap-2 px-8 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-lg text-sm font-black text-purple-600 hover:text-purple-700 transition-all shadow-sm"
        >
          {adminEmail}
          <ExternalLink size={14} />
        </a>
      </div>
    </div>
  );

  // If profile is null, it means we are in the standalone unauthenticated view (from Landing Page)
  if (!profile) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#020617] transition-colors duration-300">
        {/* Floating Navigation Header - Matches Landing Page */}
        <nav className="fixed top-6 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] md:w-[calc(100%-3rem)] max-w-6xl z-50 glass-card rounded-2xl px-6 h-16 flex items-center justify-between shadow-2xl border-white/40 dark:border-white/5">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="p-2 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-lg shadow-lg group-hover:scale-105 transition-transform">
              <Hash size={18} className="text-white" />
            </div>
            <span className="hidden sm:block text-lg font-black tracking-tight text-slate-900 dark:text-white">SharpNote</span>
          </Link>
          
          <div className="flex items-center gap-4 md:gap-8">
            <Link 
              to="/teachers" 
              className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-colors ${location.pathname === '/teachers' ? 'text-purple-600 dark:text-purple-400' : 'text-slate-500 dark:text-slate-400 hover:text-purple-600'}`}
            >
              <Store size={16} />
              {t.nav.marketplace}
            </Link>
            
            <Link 
              to="/pricing" 
              className={`hidden md:flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-colors ${location.pathname === '/pricing' ? 'text-purple-600 dark:text-purple-400' : 'text-slate-500 dark:text-slate-400 hover:text-purple-600'}`}
            >
              <CircleDollarSign size={16} />
              {t.nav.pricing}
            </Link>
            
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')}
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-all text-[10px] font-black uppercase tracking-widest"
              >
                <Languages size={16} />
                {language.toUpperCase()}
              </button>
              <button 
                onClick={() => setIsDark(!isDark)}
                className="p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-all"
              >
                {isDark ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <Link 
                to="/login" 
                className="px-6 py-2 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-950 font-black text-[10px] uppercase tracking-widest hover:scale-105 transition-all shadow-xl"
              >
                {t.landing.enterApp}
              </Link>
            </div>
          </div>
        </nav>

        <main className="pt-40 pb-20 px-6">
          <div className="max-w-4xl mx-auto">
            {content}
          </div>
        </main>
      </div>
    );
  }

  // Authenticated state: Layout component provides sidebar and standard spacing
  return (
    <div className="max-w-4xl mx-auto py-10">
      {content}
    </div>
  );
};

export default Support;
